"use client";

import React, { useState } from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Mail01Icon,
  CallIcon,
  Location01Icon,
  ArrowRight01Icon,
  GithubIcon,
  TwitterIcon,
  Linkedin01Icon,
  InstagramIcon,
  Facebook01Icon,
  CodeIcon,
  Layout01Icon,
  ShoppingCart01Icon,
  FilesIcon,
  SmartPhone01Icon,
  HeartPulseIcon,
  CompassIcon,
  AnalyticsUpIcon,
  Database01Icon,
  Copy01Icon,
  Tick01Icon,
} from "@hugeicons/core-free-icons";
import { Input, Button, Tooltip, Separator, Chip } from "@heroui/react";
import Link from "next/link";
import Logo from "./Logo";
import { appName } from "../util/Constants";
import { toast } from "sonner";

const companyLinks = [
  { label: "About Us", href: "/about-us" },
  { label: "Our Team", href: "/about-us" },
  { label: "Careers", href: "/about-us" },
  { label: "Portfolio", href: "/our-work" },
  { label: "Case Studies", href: "/our-work" },
  { label: "Blog", href: "/blog" },
];

const supportLinks = [
  { label: "Contact", href: "/contact" },
  { label: "FAQ", href: "/contact" },
  { label: "Privacy Policy", href: "/privacy-policy" },
  { label: "Terms of Service", href: "/terms-of-service" },
  { label: "Cookie Policy", href: "/cookie-policy" },
  { label: "Support Center", href: "/contact" },
];

const ServiceArray = [
  {
    id: "website-development",
    name: "Website Development",
    description: "Building custom websites from scratch or using popular frameworks/platforms.",
    category: "Development",
    icon: <HugeiconsIcon icon={CodeIcon} className="h-4 w-4" size={16} />
  },
  {
    id: "ui-ux-design",
    name: "UI/UX Design",
    description: "Creating intuitive, user-friendly, and visually appealing interfaces and experiences.",
    category: "Design",
    icon: <HugeiconsIcon icon={Layout01Icon} className="h-4 w-4" size={16} />
  },
  {
    id: "e-commerce-solutions",
    name: "E-commerce Solutions",
    description: "Developing online stores with shopping carts, payment gateways, and product management.",
    category: "Development",
    icon: <HugeiconsIcon icon={ShoppingCart01Icon} className="h-4 w-4" size={16} />
  },
  {
    id: "cms-development",
    name: "CMS Development & Integration",
    description: "Implementing and customizing Content Management Systems for easy content updates.",
    category: "Development",
    icon: <HugeiconsIcon icon={FilesIcon} className="h-4 w-4" size={16} />
  },
  {
    id: "mobile-app-development",
    name: "Mobile App Development",
    description: "Building native or cross-platform mobile applications for iOS and Android.",
    category: "Development",
    icon: <HugeiconsIcon icon={SmartPhone01Icon} className="h-4 w-4" size={16} />
  },
  {
    id: "website-maintenance-support",
    name: "Website Maintenance & Support",
    description: "Providing ongoing updates, security monitoring, backups, and technical support.",
    category: "Ongoing Services",
    icon: <HugeiconsIcon icon={HeartPulseIcon} className="h-4 w-4" size={16} />
  },
  {
    id: "web-strategy-consulting",
    name: "Web Strategy & Consulting",
    description: "Offering expert advice on project planning, technology stack selection, and digital strategy.",
    category: "Strategy",
    icon: <HugeiconsIcon icon={CompassIcon} className="h-4 w-4" size={16} />
  },
  {
    id: "seo-optimization",
    name: "Search Engine Optimization (SEO)",
    description: "Improving website visibility and ranking in search engine results.",
    category: "Related Services",
    icon: <HugeiconsIcon icon={AnalyticsUpIcon} className="h-4 w-4" size={16} />
  },
  {
    id: "web-hosting-domains",
    name: "Web Hosting & Domain Management",
    description: "Setting up and managing reliable web hosting environments and domain names.",
    category: "Related Services",
    icon: <HugeiconsIcon icon={Database01Icon} className="h-4 w-4" size={16} />
  },
];

const socialLinks = [
  { icon: GithubIcon, href: "https://github.com/Jignesh220", label: "GitHub" },
  { icon: Linkedin01Icon, href: "https://www.linkedin.com/in/jignesh-baria/", label: "LinkedIn" },
  { icon: InstagramIcon, href: "https://www.instagram.com/devebsy/", label: "Instagram" },
];

const tags = [
  "Next.js",
  "React",
  "UI/UX Design",
  "E-commerce",
  "Shopify",
  "Node.js",
  "WordPress",
  "Headless CMS",
  "SEO Optimization",
  "AWS",
  "Vercel",
  "API Integrations",
  "Web Applications",
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const columnVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 260,
      damping: 22,
    },
  },
} as const;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.", {
        position: "bottom-right",
      });
      return;
    }
    toast.success("Subscribed successfully!", {
      description: `Thank you for subscribing with ${email}`,
      position: "bottom-right",
    });
    setEmail("");
  };

  const handleCopy = (text: string, isEmail: boolean) => {
    navigator.clipboard.writeText(text);
    if (isEmail) {
      setCopiedEmail(true);
      toast.success("Email copied to clipboard!", {
        description: text,
        position: "bottom-right",
      });
      setTimeout(() => setCopiedEmail(false), 2000);
    } else {
      setCopiedPhone(true);
      toast.success("Phone number copied to clipboard!", {
        description: text,
        position: "bottom-right",
      });
      setTimeout(() => setCopiedPhone(false), 2000);
    }
  };

  return (
    <footer className="relative w-full py-16 px-4 md:px-8 overflow-hidden bg-neutral-50 dark:bg-black text-neutral-800 dark:text-neutral-200 border-t border-neutral-200/50 dark:border-neutral-900/50 transition-colors duration-500 font-outfit tracking-wide">
      
      {/* Decorative Grid Pattern */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.02] dark:opacity-[0.04]"
        style={{
          backgroundImage: "radial-gradient(circle, #64748b 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      {/* Floating Background Blur Blobs */}
      <motion.div
        className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/5 dark:to-purple-500/5 rounded-full blur-3xl pointer-events-none"
        animate={{
          y: [0, -25, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 7,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
        }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 dark:from-cyan-500/5 dark:to-blue-500/5 rounded-full blur-3xl pointer-events-none"
        animate={{
          y: [0, 25, 0],
          scale: [1, 0.9, 1],
        }}
        transition={{
          duration: 9,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
        }}
      />

      <motion.div
        className="max-w-7xl mx-auto flex flex-col relative z-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          
          {/* Column 1: Company Branding & Contact */}
          <motion.div variants={columnVariants} className="lg:col-span-4 flex flex-col text-left">
            <Link href="/" className="flex items-center space-x-3 mb-6 hover:opacity-90 transition-opacity w-fit">
              <Logo />
              <span className="font-recoleta text-2xl tracking-wider font-extrabold bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 dark:from-blue-400 dark:via-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">
                {appName}
              </span>
            </Link>

            <p className="text-neutral-500 dark:text-neutral-400 text-sm leading-relaxed mb-6 max-w-sm">
              We craft exceptional digital experiences through innovative web development, stunning UI/UX design, and cutting-edge software solutions.
            </p>

            <div className="flex flex-col gap-3.5">
              
              {/* Copyable Email */}
              <Tooltip delay={150}>
                <Tooltip.Trigger>
                  <button
                    onClick={() => handleCopy("devebsy@gmail.com", true)}
                    className="flex items-center space-x-3 text-neutral-600 dark:text-neutral-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300 font-outfit text-sm cursor-pointer group w-fit text-left outline-none"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-neutral-900/50 border border-blue-100/50 dark:border-neutral-800/80 flex items-center justify-center text-blue-600 dark:text-blue-450 group-hover:scale-105 transition-all duration-300">
                      <HugeiconsIcon icon={copiedEmail ? Tick01Icon : Mail01Icon} className="w-4 h-4" size={16} />
                    </div>
                    <span className="font-medium">devebsy@gmail.com</span>
                    <HugeiconsIcon icon={Copy01Icon} className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-neutral-400" size={14} />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content showArrow>
                  <Tooltip.Arrow />
                  <p className="text-xs font-outfit">Click to copy email address</p>
                </Tooltip.Content>
              </Tooltip>

              {/* Copyable Phone */}
              <Tooltip delay={150}>
                <Tooltip.Trigger>
                  <button
                    onClick={() => handleCopy("+918849597903", false)}
                    className="flex items-center space-x-3 text-neutral-600 dark:text-neutral-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-300 font-outfit text-sm cursor-pointer group w-fit text-left outline-none"
                  >
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-neutral-900/50 border border-indigo-100/50 dark:border-neutral-800/80 flex items-center justify-center text-indigo-600 dark:text-indigo-455 group-hover:scale-105 transition-all duration-300">
                      <HugeiconsIcon icon={copiedPhone ? Tick01Icon : CallIcon} className="w-4 h-4" size={16} />
                    </div>
                    <span className="font-medium">+91 88495 97903</span>
                    <HugeiconsIcon icon={Copy01Icon} className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-neutral-400" size={14} />
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Content showArrow>
                  <Tooltip.Arrow />
                  <p className="text-xs font-outfit">Click to copy phone number</p>
                </Tooltip.Content>
              </Tooltip>

              {/* Location */}
              <div className="flex items-center space-x-3 text-neutral-600 dark:text-neutral-400 font-outfit text-sm w-fit">
                <div className="w-8 h-8 rounded-lg bg-violet-50 dark:bg-neutral-900/50 border border-violet-100/50 dark:border-neutral-800/80 flex items-center justify-center text-violet-600 dark:text-violet-455">
                  <HugeiconsIcon icon={Location01Icon} className="w-4 h-4" size={16} />
                </div>
                <span className="font-medium">Gujarat, India</span>
              </div>

            </div>
          </motion.div>

          {/* Column 2: Services */}
          <motion.div variants={columnVariants} className="lg:col-span-3 flex flex-col text-left lg:pl-4">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-neutral-800 dark:text-neutral-100 mb-6 font-outfit">
              Services
            </h3>
            <ul className="space-y-3.5">
              {ServiceArray.slice(0, 6).map((service) => (
                <motion.li
                  key={service.id}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  title={service.description}
                  className="text-neutral-500 dark:text-neutral-400 font-outfit text-[14px] hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300 cursor-pointer"
                >
                  <Link href="/services" className="flex items-center gap-2.5 group">
                    <span className="text-neutral-400 dark:text-neutral-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
                      {service.icon}
                    </span>
                    <span className="font-medium">{service.name}</span>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Column 3: Company */}
          <motion.div variants={columnVariants} className="lg:col-span-2 flex flex-col text-left">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-neutral-800 dark:text-neutral-100 mb-6 font-outfit">
              Company
            </h3>
            <ul className="space-y-3.5">
              {companyLinks.map((link) => (
                <motion.li
                  key={link.label}
                  whileHover={{ x: 6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="text-neutral-500 dark:text-neutral-400 font-outfit text-[14px] hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300 cursor-pointer font-medium"
                >
                  <Link href={link.href} className="block">
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Column 4: Stay Connected & Socials */}
          <motion.div variants={columnVariants} className="lg:col-span-3 flex flex-col text-left">
            <h3 className="text-sm font-extrabold uppercase tracking-widest text-neutral-800 dark:text-neutral-100 mb-6 font-outfit">
              Stay Connected
            </h3>

            <p className="text-neutral-500 dark:text-neutral-400 text-sm leading-relaxed mb-4">
              Subscribe to our newsletter for the latest tech updates and digital insights.
            </p>

            {/* Newsletter input/button */}
            <form onSubmit={handleSubscribe} className="flex gap-2 w-full">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="secondary"
                aria-label="Email Address"
                className="flex-grow font-outfit bg-neutral-100/50 dark:bg-neutral-900/30 border border-neutral-200 dark:border-neutral-800 rounded-xl px-3.5 py-2.5 text-sm text-neutral-800 dark:text-neutral-200 transition-colors duration-300 w-full outline-none focus-visible:border-blue-500 dark:focus-visible:border-blue-500"
              />
              <Button
                type="submit"
                isIconOnly
                aria-label="Subscribe Button"
                className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white rounded-xl shadow-md cursor-pointer flex items-center justify-center w-10 min-w-10 h-10 hover:opacity-95 active:scale-95 transition-all duration-200"
              >
                <HugeiconsIcon icon={ArrowRight01Icon} className="w-4 h-4" size={16} />
              </Button>
            </form>

            {/* Social Icons with Tooltips */}
            <div className="flex space-x-3 mt-6">
              {socialLinks.map((social, index) => (
                <Tooltip key={social.label} delay={100}>
                  <Tooltip.Trigger>
                    <Link href={social.href} target="_blank" rel="me noopener noreferrer" aria-label={social.label}>
                      <motion.div
                        className="w-10 h-10 bg-neutral-100/50 dark:bg-neutral-900/40 border border-neutral-200/40 dark:border-neutral-800/50 rounded-xl flex items-center justify-center text-neutral-600 dark:text-neutral-400 hover:text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-indigo-600 hover:border-transparent transition-all duration-300 cursor-pointer"
                        whileHover={{ scale: 1.12, rotate: 6 }}
                        whileTap={{ scale: 0.93 }}
                        initial={{ opacity: 0, y: 15 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ type: "spring", stiffness: 350, damping: 18, delay: index * 0.08 }}
                      >
                        <HugeiconsIcon icon={social.icon} className="w-5 h-5 transition-transform duration-300" size={20} />
                      </motion.div>
                    </Link>
                  </Tooltip.Trigger>
                  <Tooltip.Content showArrow>
                    <Tooltip.Arrow />
                    <p className="text-xs font-outfit">Follow on {social.label}</p>
                  </Tooltip.Content>
                </Tooltip>
              ))}
            </div>
          </motion.div>

        </div>

        {/* Popular Tags Section */}
        <div className="mt-12 pt-8 border-t border-neutral-200/50 dark:border-neutral-900/50">
          <h4 className="text-[11px] font-extrabold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest mb-4 text-center md:text-left font-outfit">
            Popular Technologies & Solutions
          </h4>
          <div className="flex flex-wrap justify-center md:justify-start gap-2">
            {tags.map((tag) => (
              <motion.div
                key={tag}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 15 }}
              >
                <Chip
                  variant="soft"
                  className="bg-neutral-100/50 dark:bg-neutral-900/30 text-[11px] text-neutral-600 dark:text-neutral-300 px-3.5 py-1.5 border border-neutral-200/40 dark:border-neutral-800/50 hover:bg-gradient-to-r hover:from-blue-600 hover:to-indigo-600 hover:text-white hover:border-transparent rounded-xl transition-all duration-300 cursor-pointer font-outfit font-medium"
                >
                  {tag}
                </Chip>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Separator Divider */}
        <Separator className="bg-neutral-200/60 dark:bg-neutral-900/60 my-8" />

        {/* Bottom bar: Copyright & Support Links */}
        <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <p className="text-neutral-500 dark:text-neutral-400 text-xs font-medium text-center md:text-left">
            © {new Date().getFullYear()} {appName}. All rights reserved. Crafted with ❤️ in India
          </p>

          <div className="flex items-center space-x-6 text-xs">
            {supportLinks.slice(2, 5).map((link) => (
              <Link
                key={link.label}
                href={link.href}
                className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-white transition-colors duration-200 font-medium"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

      </motion.div>
    </footer>
  );
}
